import { Box, type DrawerProps, IconButton, Stack, Typography } from "@mui/material";
import { Drawer, DrawerContent } from "Components/Analyses/_Shared/Drawer";
import { Button } from "Components/_Shared/Buttons/Button";
import { Flex } from "Components/_Shared/Core/Flex";
import { ReactComponent as CloseIcon } from "Components/_Shared/Icons/Assets/close.svg";
import { ContractProposalBaseInvestmentToolType, type ContractProposalBaseSolutionHorizonType } from "Infrastructure/ApiZfp/Schema";
import type { NsurePlanOverview } from "Infrastructure/ApiZfp/Schema/nsurePlanOverview";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { type FC, startTransition, useRef, useState } from "react";
import { InvestmentsNsureWrapper } from "./Forms/ProposedSolution/InvestmentsNsure/InvestmentsNsureWrapper";
import { ProposedSolutionNsureReadOnly } from "./Forms/ProposedSolution/InvestmentsNsure/ProposedSolutionNsureReadOnly";
import { type ProposedSolutionFormHandle, ProposedSolutionFormWrapper } from "./Forms/ProposedSolution/ProposedSolutionFormWrapper";

type Props = {
  onClose: () => void;
  solutionId?: string;
  nsurePlan?: NsurePlanOverview;
  investmentToolType: ContractProposalBaseInvestmentToolType;
  horizonType: ContractProposalBaseSolutionHorizonType;
  isNsure?: boolean;
} & DrawerProps;

export type SubmitProposedSolutionFormHandle = {
  readonly submitForm: () => void;
  readonly deleteSolution?: () => void;
};

export const ProposedSolutionDrawer: FC<Props> = ({
  onClose,
  horizonType,
  solutionId,
  nsurePlan,
  investmentToolType,
  isNsure,
  ...rest
}) => {
  const { t } = useResource();
  const proposedSolutionFormRef = useRef<SubmitProposedSolutionFormHandle & ProposedSolutionFormHandle>(null);

  const [contentReady, setContentReady] = useState(false);
  const [currentFormType, setCurrentFormType] = useState<ContractProposalBaseInvestmentToolType>(investmentToolType);
  const isEdit = !!solutionId;
  const isNsureCandidate = isNsure ? true : currentFormType === ContractProposalBaseInvestmentToolType.investment && !isEdit;

  // Edge case: the proposal carries a portfolio, but its horizon has no Nsure plan any more. The Nsure flow
  // cannot be loaded without the plan, so only a read-only detail with a delete option is offered.
  const mode: "nsure" | "nsureReadOnly" | "standard" =
    isNsureCandidate && nsurePlan ? "nsure" : isNsure && solutionId ? "nsureReadOnly" : "standard";

  const handleClose = () => {
    const active = document.activeElement as HTMLElement | null;
    if (active && typeof active.blur === "function") active.blur();
    onClose();
  };

  return (
    <Drawer
      {...rest}
      onClose={handleClose}
      ModalProps={{ keepMounted: false, disableRestoreFocus: false }}
      SlideProps={{ onEntered: () => setContentReady(true), onExited: () => setContentReady(false) }}
      paperWidth={mode === "nsure" ? 320 : undefined}
    >
      <Flex justifyContent="space-between" alignItems="center" p={Spacing.XXL}>
        <Typography variant="h2">
          {mode === "nsureReadOnly"
            ? t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.ReadOnly.Title)
            : isEdit
              ? t(Resources.AnalysisDetail.GoalsFulfillment.ProposedSolutionEdit)
              : t(Resources.AnalysisDetail.GoalsFulfillment.Proposed)}
        </Typography>

        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </Flex>

      {mode === "nsure" && nsurePlan ? (
        contentReady && (
          <InvestmentsNsureWrapper
            nsurePlan={nsurePlan}
            onFormTypeChange={setCurrentFormType}
            onClose={handleClose}
            horizonType={horizonType}
            edit={isEdit}
            proposedSolutionId={solutionId}
          />
        )
      ) : mode === "nsureReadOnly" && solutionId ? (
        contentReady && <ProposedSolutionNsureReadOnly proposedSolutionId={solutionId} onClose={handleClose} />
      ) : (
        <DrawerContent>
          <Box px={Spacing.XXL} height={"100%"}>
            {contentReady && (
              <ProposedSolutionFormWrapper
                ref={proposedSolutionFormRef}
                edit={isEdit}
                proposedSolutionID={solutionId}
                onSuccess={handleClose}
                onFormTypeChange={(type) => startTransition(() => setCurrentFormType(type))}
                initialFormType={currentFormType}
                horizonType={horizonType}
              />
            )}
          </Box>
        </DrawerContent>
      )}

      {mode === "standard" &&
        (isEdit ? (
          <Flex flexDirection="row" justifyContent="space-between" py={Spacing.XXL} px={Spacing.XXL}>
            <Button variant="text" onClick={() => proposedSolutionFormRef.current?.deleteSolution?.()} color="error">
              {t(Resources.Common.Delete)}
            </Button>
            <Stack direction="row" spacing={Spacing.XL}>
              <Button
                onClick={() => handleClose()}
                color="secondary"
                sx={{
                  marginRight: Spacing.XL,
                }}
              >
                {t(Resources.Common.Close)}
              </Button>

              <Button type="submit" onClick={() => proposedSolutionFormRef.current?.submitForm()}>
                {t(Resources.Common.Save)}
              </Button>
            </Stack>
          </Flex>
        ) : (
          <Flex flexDirection="row" justifyContent="flex-end" py={Spacing.XXL} px={Spacing.XXL}>
            <Button
              onClick={() => handleClose()}
              color="secondary"
              sx={{
                marginRight: Spacing.XL,
              }}
            >
              {t(Resources.Common.Close)}
            </Button>

            <Button type="submit" onClick={() => proposedSolutionFormRef.current?.submitForm()}>
              {t(Resources.Common.Create)}
            </Button>
          </Flex>
        ))}
    </Drawer>
  );
};
